import {
  BaseButtonProps,
  ButtonBarrelComponent,
  ButtonKind,
  ButtonSize,
  ButtonProps,
} from 'types/ui'

import { useMemo } from 'react'
import classNames from 'classnames'
import { LoadingIcon } from '@icons/loading'

function _Button({
  type = 'button',
  role = 'button',
  kind = ButtonKind.Primary,
  size = ButtonSize.Base,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  loading = false,
  className,
  onClick,
  children,
  ...props
}: BaseButtonProps) {
  const disabled = props.disabled

  const finalClassName = useMemo(
    () => classNames('button will-change-transform', className),
    [className]
  )

  return (
    <button
      {...props}
      data-kind={kind}
      data-size={size}
      data-loading={loading}
      type={type}
      role={role}
      className={finalClassName}
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled}
    >
      <div className="relative flex items-center justify-center w-full space-x-2">
        {!loading && LeftIcon && (
          <LeftIcon className="w-3.5 h-3.5 min-h-[14px] min-w-[14px] stroke-2" />
        )}
        <span className={loading ? 'opacity-0' : 'opacity-100'}>{children}</span>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center w-full !m-0">
            <LoadingIcon />
          </div>
        )}
        {!loading && RightIcon && (
          <RightIcon className="w-3.5 min-h-[14px] min-w-[14px] h-3.5 stroke-2" />
        )}
      </div>
    </button>
  )
}

function useButtonSize({ sm, md, lg }: { sm: boolean; md: boolean; lg: boolean }) {
  return useMemo(() => {
    switch (true) {
      case lg:
        return ButtonSize.Large
      case sm:
        return ButtonSize.Small
      case md:
      default:
        return ButtonSize.Base
    }
  }, [lg, md, sm])
}

function PrimaryButton({ sm = false, md = true, lg = false, ...props }: ButtonProps) {
  const size = useButtonSize({ sm, md, lg })
  return <_Button {...props} kind={ButtonKind.Primary} size={size} />
}

function SecondaryButton({ sm = false, md = true, lg = false, ...props }: ButtonProps) {
  const size = useButtonSize({ sm, md, lg })
  return <_Button {...props} kind={ButtonKind.Secondary} size={size} />
}

function TertiaryButton({ sm = false, md = true, lg = false, ...props }: ButtonProps) {
  const size = useButtonSize({ sm, md, lg })
  return <_Button {...props} kind={ButtonKind.Tertiary} size={size} />
}

function FloatingButton({ sm = false, md = true, lg = false, ...props }: ButtonProps) {
  const size = useButtonSize({ sm, md, lg })
  return <_Button {...props} kind={ButtonKind.Floating} size={size} />
}

function SubmitButton({
  sm = false,
  md = true,
  lg = false,
  ...props
}: Omit<ButtonProps, 'onClick'>) {
  const size = useButtonSize({ sm, md, lg })
  return <_Button {...props} type="submit" size={size} role="submit" />
}

export const Button: ButtonBarrelComponent = Object.assign(_Button, {
  Primary: PrimaryButton,
  Secondary: SecondaryButton,
  Tertiary: TertiaryButton,
  Floating: FloatingButton,
  Submit: SubmitButton,
})
